import { useMemo, useState } from "react";
import { CheckCircle, Clock, Download, Plus, Timer, Warning } from "@phosphor-icons/react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { EmptyState, StateBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COLUMNS = ["Sl no", "Element", "ACS Q no", "Strategic value area", "Monthly commitment",
  "SLT owner", "Planned date", "Actual completion", "Attachment", "Completion status",
  "Status", "Timeliness"];
const GRID = "md:grid-cols-[3rem_5rem_5rem_9rem_minmax(12rem,1.6fr)_8rem_7rem_7rem_6rem_8rem_6rem_6rem]";

const timeliness = (c) => {
  const planned = c.planned_date || c.due_date;
  if (!c.actual_completion_date) return "Pending";
  if (!planned) return "On time";
  return c.actual_completion_date <= planned ? "On time" : "Delayed";
};

const Tile = ({ label, value, icon: Icon, tone, testid }) => (
  <div className={`rounded-2xl border px-5 py-4 ${tone}`} data-testid={testid}>
    <p className="font-mono text-3xl tabular-nums">{value}</p>
    <p className="mt-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em]">
      {Icon && <Icon size={12} />} {label}
    </p>
  </div>
);

export const CommitmentsPanel = ({ base, tenantId, framework, elementCode, commitments, reload, can }) => {
  const [tab, setTab] = useState("list");
  const [range, setRange] = useState({ from: "", to: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", question_no: "", strategic_value_area: "", owner_name: "",
    planned_date: "", actual_completion_date: "", completion_status: "not_started",
  });

  const rows = useMemo(() => {
    const key = (c) => c.planned_date || c.due_date || "";
    return commitments.filter((c) =>
      (!range.from || key(c) >= range.from) && (!range.to || key(c) <= range.to));
  }, [commitments, range]);

  const stats = useMemo(() => ({
    total: rows.length,
    completed: rows.filter((c) => c.status === "done" || c.completion_status === "completed").length,
    pending: rows.filter((c) => c.status !== "done" && c.completion_status !== "completed").length,
    onTime: rows.filter((c) => timeliness(c) === "On time").length,
    delayed: rows.filter((c) => timeliness(c) === "Delayed").length,
  }), [rows]);

  const add = async () => {
    try {
      await api.post(`/tenants/${tenantId}/action-items`, {
        ...form, framework, element_code: elementCode, confidentiality: 1,
        due_date: form.planned_date || null,
      });
      setOpen(false);
      setForm({ title: "", question_no: "", strategic_value_area: "", owner_name: "",
        planned_date: "", actual_completion_date: "", completion_status: "not_started" });
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const patch = async (id, body) => {
    try {
      await api.put(`/tenants/${tenantId}/action-items/${id}`, body);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const upload = async (id, file) => {
    const body = new FormData();
    body.append("file", file);
    body.append("scope_type", "commitment");
    body.append("scope_key", `${framework}:${elementCode}:${id}`);
    body.append("framework", framework);
    try {
      await api.post(`/tenants/${tenantId}/attachments`, body);
      toast.success("Attachment saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const table = (
    <>
      <div className={`hidden gap-3 border-b border-border px-6 py-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid ${GRID}`}>
        {COLUMNS.map((c) => <span key={c}>{c}</span>)}
      </div>
      <div className="divide-y divide-border">
        {rows.map((c, i) => (
          <div key={c.id} data-testid={`commit-row-${c.id}`}
               className={`grid grid-cols-1 gap-2 px-6 py-4 transition-colors hover:bg-sky/10 md:items-center md:gap-3 ${GRID}`}>
            <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
            <span className="text-xs text-foreground/70">{c.element_code || "—"}</span>
            <span className="font-mono text-xs text-muted-foreground">{c.question_no || "—"}</span>
            <span className="text-xs text-foreground/70">{c.strategic_value_area || "—"}</span>
            <span className="text-sm text-navy">{c.title}</span>
            <span className="text-xs text-foreground/70">{c.owner_name || "—"}</span>
            <span className="font-mono text-xs text-muted-foreground">{c.planned_date || c.due_date || "—"}</span>
            <Input type="date" data-testid={`commit-actual-${c.id}`} value={c.actual_completion_date || ""}
                   disabled={!can("edit")}
                   onChange={(e) => patch(c.id, { actual_completion_date: e.target.value })}
                   className="h-8 text-xs" />
            <label className="cursor-pointer text-[10px] uppercase tracking-[0.14em] text-deepblue hover:underline">
              Upload
              <input type="file" className="hidden" data-testid={`commit-file-${c.id}`}
                     onChange={(e) => e.target.files?.[0] && upload(c.id, e.target.files[0])} />
            </label>
            <Select value={c.completion_status || "not_started"} disabled={!can("edit")}
                    onValueChange={(v) => patch(c.id, {
                      completion_status: v, status: v === "completed" ? "done" : "open" })}>
              <SelectTrigger data-testid={`commit-completion-${c.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["not_started", "in_progress", "completed"].map((s) => (
                  <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <StateBadge state={c.status} />
            <span className={`text-[10px] uppercase tracking-[0.14em] ${
              timeliness(c) === "Delayed" ? "text-destructive"
                : timeliness(c) === "On time" ? "text-success" : "text-muted-foreground"}`}>
              {timeliness(c)}
            </span>
          </div>
        ))}
        {rows.length === 0 && (
          <EmptyState testid="lec-commitments-empty" title="No commitments in this range"
                      message="Record the monthly commitments agreed in the review." />
        )}
      </div>
    </>
  );

  return (
    <section className="panel overflow-hidden" data-testid="lec-commitments">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-5">
        <div>
          <h1 className="font-display text-2xl text-navy">Commitments</h1>
          <p className="text-xs text-muted-foreground">
            Monthly commitments, owners and timeliness for this element.
          </p>
        </div>
        {can("create") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="lec-add-commitment" className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]">
                <Plus size={13} className="mr-1.5" /> Add commitment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">New commitment</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="label-caps">Monthly commitment</Label>
                  <Textarea data-testid="lec-commitment-title" value={form.title}
                            onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                {[["Asset creation system Q no", "question_no"],
                  ["Strategic value area", "strategic_value_area"],
                  ["SLT owner", "owner_name"]].map(([label, key]) => (
                  <div key={key} className="space-y-2">
                    <Label className="label-caps">{label}</Label>
                    <Input data-testid={`lec-commitment-${key}`} value={form[key]}
                           onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  {[["Planned date", "planned_date"], ["Actual completion", "actual_completion_date"]].map(([label, key]) => (
                    <div key={key} className="space-y-2">
                      <Label className="label-caps">{label}</Label>
                      <Input type="date" data-testid={`lec-commitment-${key}`} value={form[key]}
                             onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
                    </div>
                  ))}
                </div>
                <Button data-testid="lec-commitment-submit" className="w-full" disabled={!form.title}
                        onClick={add}>Add commitment</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-1 border-b border-border px-6 pt-4">
        {[["list", "Commitments"], ["summary", "Commitments summary"]].map(([key, label]) => (
          <button key={key} data-testid={`commit-tab-${key}`} onClick={() => setTab(key)}
                  className={`rounded-t-lg px-4 py-2.5 text-[11px] uppercase tracking-[0.14em] transition-colors ${
                    tab === key ? "border-b-2 border-navy text-navy" : "text-muted-foreground hover:text-navy"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <div className="space-y-6 p-6" data-testid="commit-summary">
          <div className="flex flex-wrap items-end gap-3 rounded-2xl bg-muted/40 p-4">
            {[["From date", "from"], ["To date", "to"]].map(([label, key]) => (
              <div key={key} className="space-y-1.5">
                <Label className="label-caps">{label}</Label>
                <Input type="date" data-testid={`commit-${key}-date`} value={range[key]}
                       onChange={(e) => setRange({ ...range, [key]: e.target.value })}
                       className="h-9 w-40 text-xs" />
              </div>
            ))}
            <Button variant="outline" data-testid="commit-range-reset"
                    className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]"
                    onClick={() => setRange({ from: "", to: "" })}>Reset</Button>
            <Button asChild className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]">
              <a data-testid="commit-download-excel"
                 href={fileUrl(`${base}/commitments/export?from_date=${range.from}&to_date=${range.to}`)}>
                <Download size={13} className="mr-1.5" /> Download summary (Excel)
              </a>
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
            <Tile testid="commit-stat-total" label="Total commitments" value={stats.total}
                  tone="border-deepblue/25 bg-deepblue/8 text-navy" />
            <Tile testid="commit-stat-completed" label="Completed" value={stats.completed} icon={CheckCircle}
                  tone="border-success/30 bg-success/10 text-success" />
            <Tile testid="commit-stat-pending" label="Pending" value={stats.pending} icon={Timer}
                  tone="border-gold/40 bg-gold/12 text-navy" />
            <Tile testid="commit-stat-ontime" label="On time" value={stats.onTime} icon={Clock}
                  tone="border-success/25 bg-sky/15 text-navy" />
            <Tile testid="commit-stat-delayed" label="Delayed" value={stats.delayed} icon={Warning}
                  tone="border-destructive/30 bg-destructive/8 text-destructive" />
          </div>

          <div>
            <p className="label-caps mb-3">Commitments in selected range</p>
            <div className="overflow-x-auto rounded-2xl border border-border">{table}</div>
          </div>
        </div>
      )}

      {tab === "list" && <div className="overflow-x-auto">{table}</div>}
    </section>
  );
};
